import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '../types/api';
import { ApiError } from '../types/api';
import { isDemoMode } from '../lib/demoMode';
import { resolveDemoResponse } from '../lib/demoData';
import { openAuthPrompt } from '../lib/authPrompt';
import { isNative } from '../lib/platform';
import { getHasSession } from '../lib/sessionFlag';

// On web, /api is the same origin as the dashboard. Inside Capacitor the
// WebView origin is capacitor://localhost, so VITE_API_BASE_URL must point at
// the real API (e.g. https://00pays.com/api/v1) at build time.
const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const client = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Demo interceptor — only mocks the two storeId-less endpoints (/auth/me,
// /stores). Store-scoped requests pass through to the real API, where the
// backend `demoStoreGate` middleware serves seeded demo-store data and
// silently acknowledges writes.
client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!isDemoMode()) return config;

  const url = (config.baseURL || '') + (config.url || '');
  const payload = resolveDemoResponse(config.method || 'get', url);
  if (payload === undefined) return config;

  const syntheticResponse: AxiosResponse = {
    data: { status: 'ok', data: payload } as ApiResponse,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: null,
  };

  (config as any).adapter = () => Promise.resolve(syntheticResponse);
  return config;
});

client.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse;
    if (body.status === 'error') {
      throw new ApiError(
        body.message || 'Unknown error',
        body.code || 'UNKNOWN_ERROR',
        response.status,
        body.details,
      );
    }
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const body = error.response?.data as ApiResponse | undefined;

      if (status === 401) {
        // In demo mode we never want to prompt — if the backend 401s (e.g.
        // the demo store hasn't been seeded or the demoStore flag isn't
        // set), let the query fail silently and show an empty state.
        //
        // On native, suppress the auto-pop at cold start (before we've ever
        // seen a session): MobileWelcome handles that entry. Once a session
        // has existed in this app instance, mid-use 401s still pop the
        // modal so the user can re-auth without losing their place.
        const shouldPrompt = !isDemoMode() && (!isNative || getHasSession());
        if (shouldPrompt) {
          openAuthPrompt('signin');
        }
        throw new ApiError(
          body?.message || 'Unauthenticated',
          body?.code || 'UNAUTHENTICATED',
          401,
          body?.details,
        );
      }

      throw new ApiError(
        body?.message || error.message,
        body?.code || 'NETWORK_ERROR',
        status || 0,
        body?.details,
      );
    }
    throw error;
  },
);

export default client;
